/**
 * LanguageEngine - Dedicated language management system
 * 
 * Features:
 * - Auto-detect browser language
 * - Monitor Google Translate changes
 * - Coordinate text fitting across the app
 * - RTL language support
 */

export type LanguageCode = string;

export interface LanguageInfo {
  code: string;
  name: string;
  nativeName: string;
  isRTL: boolean;
  avgCharWidth: number; // Relative to English (1.0)
}

// Language database with character width ratios (affects text fitting)
export const LANGUAGE_DATABASE: Record<string, LanguageInfo> = {
  en: { code: 'en', name: 'English', nativeName: 'English', isRTL: false, avgCharWidth: 1.0 },
  es: { code: 'es', name: 'Spanish', nativeName: 'Español', isRTL: false, avgCharWidth: 1.1 },
  fr: { code: 'fr', name: 'French', nativeName: 'Français', isRTL: false, avgCharWidth: 1.15 },
  de: { code: 'de', name: 'German', nativeName: 'Deutsch', isRTL: false, avgCharWidth: 1.2 },
  it: { code: 'it', name: 'Italian', nativeName: 'Italiano', isRTL: false, avgCharWidth: 1.1 },
  pt: { code: 'pt', name: 'Portuguese', nativeName: 'Português', isRTL: false, avgCharWidth: 1.1 },
  ru: { code: 'ru', name: 'Russian', nativeName: 'Русский', isRTL: false, avgCharWidth: 1.0 },
  zh: { code: 'zh', name: 'Chinese', nativeName: '中文', isRTL: false, avgCharWidth: 0.5 },
  'zh-CN': { code: 'zh-CN', name: 'Chinese (Simplified)', nativeName: '简体中文', isRTL: false, avgCharWidth: 0.5 },
  'zh-TW': { code: 'zh-TW', name: 'Chinese (Traditional)', nativeName: '繁體中文', isRTL: false, avgCharWidth: 0.5 },
  ja: { code: 'ja', name: 'Japanese', nativeName: '日本語', isRTL: false, avgCharWidth: 0.5 },
  ko: { code: 'ko', name: 'Korean', nativeName: '한국어', isRTL: false, avgCharWidth: 0.6 },
  ar: { code: 'ar', name: 'Arabic', nativeName: 'العربية', isRTL: true, avgCharWidth: 0.8 },
  he: { code: 'he', name: 'Hebrew', nativeName: 'עברית', isRTL: true, avgCharWidth: 0.9 },
  fa: { code: 'fa', name: 'Persian', nativeName: 'فارسی', isRTL: true, avgCharWidth: 0.8 },
  ur: { code: 'ur', name: 'Urdu', nativeName: 'اردو', isRTL: true, avgCharWidth: 0.8 },
  hi: { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', isRTL: false, avgCharWidth: 0.9 },
  th: { code: 'th', name: 'Thai', nativeName: 'ไทย', isRTL: false, avgCharWidth: 0.7 },
  vi: { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', isRTL: false, avgCharWidth: 1.0 },
  nl: { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', isRTL: false, avgCharWidth: 1.15 },
  pl: { code: 'pl', name: 'Polish', nativeName: 'Polski', isRTL: false, avgCharWidth: 1.1 },
  tr: { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', isRTL: false, avgCharWidth: 1.05 },
  uk: { code: 'uk', name: 'Ukrainian', nativeName: 'Українська', isRTL: false, avgCharWidth: 1.0 },
  el: { code: 'el', name: 'Greek', nativeName: 'Ελληνικά', isRTL: false, avgCharWidth: 1.1 },
  cs: { code: 'cs', name: 'Czech', nativeName: 'Čeština', isRTL: false, avgCharWidth: 1.05 },
  sv: { code: 'sv', name: 'Swedish', nativeName: 'Svenska', isRTL: false, avgCharWidth: 1.1 },
  da: { code: 'da', name: 'Danish', nativeName: 'Dansk', isRTL: false, avgCharWidth: 1.1 },
  fi: { code: 'fi', name: 'Finnish', nativeName: 'Suomi', isRTL: false, avgCharWidth: 1.15 },
  no: { code: 'no', name: 'Norwegian', nativeName: 'Norsk', isRTL: false, avgCharWidth: 1.1 },
  hu: { code: 'hu', name: 'Hungarian', nativeName: 'Magyar', isRTL: false, avgCharWidth: 1.1 },
  ro: { code: 'ro', name: 'Romanian', nativeName: 'Română', isRTL: false, avgCharWidth: 1.1 },
  id: { code: 'id', name: 'Indonesian', nativeName: 'Indonesia', isRTL: false, avgCharWidth: 1.0 },
  ms: { code: 'ms', name: 'Malay', nativeName: 'Melayu', isRTL: false, avgCharWidth: 1.0 },
  tl: { code: 'tl', name: 'Filipino', nativeName: 'Filipino', isRTL: false, avgCharWidth: 1.0 },
  bn: { code: 'bn', name: 'Bengali', nativeName: 'বাংলা', isRTL: false, avgCharWidth: 0.85 },
};

// RTL Languages list
export const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur', 'ps', 'sd', 'yi', 'dv'];

class LanguageEngineClass {
  private currentLanguage: string = 'en';
  private detectedBrowserLanguage: string = 'en';
  private listeners: Set<(lang: string) => void> = new Set();
  private translateObserver: MutationObserver | null = null;
  private isInitialized = false;

  constructor() {
    this.detectBrowserLanguage();
  }

  /**
   * Initialize the engine - should be called once on app startup
   */
  initialize(): void {
    if (this.isInitialized) return;
    
    this.detectBrowserLanguage();
    this.setupTranslateObserver();
    this.checkGoogleTranslateCookie();
    this.applyRTL();
    this.isInitialized = true;
    
    console.log('[LanguageEngine] Initialized with language:', this.currentLanguage);
  }

  /**
   * Detect user's browser language
   */
  private detectBrowserLanguage(): void {
    const browserLang = navigator.language || (navigator as any).userLanguage || 'en';
    this.detectedBrowserLanguage = browserLang.split('-')[0].toLowerCase();
    
    // If no translation is active, use browser language
    if (!this.getGoogleTranslateLanguage()) {
      this.currentLanguage = this.detectedBrowserLanguage;
    }
  }

  /**
   * Get the current Google Translate language from cookie
   */
  private getGoogleTranslateLanguage(): string | null {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'googtrans') {
        // Cookie format: /en/es (from/to)
        const parts = decodeURIComponent(value).split('/');
        if (parts.length >= 3) {
          return parts[2]; // Target language
        }
      }
    }
    return null;
  }

  /**
   * Check Google Translate cookie and update current language
   */
  private checkGoogleTranslateCookie(): void {
    const translateLang = this.getGoogleTranslateLanguage();
    if (translateLang && translateLang !== this.currentLanguage) {
      this.setCurrentLanguage(translateLang);
    }
  }

  /**
   * Setup MutationObserver to detect Google Translate changes
   */
  private setupTranslateObserver(): void {
    // Observe class changes on html element (Google Translate adds classes)
    this.translateObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes') {
          // Check if Google Translate class changed
          if (mutation.attributeName === 'class' || mutation.attributeName === 'lang') {
            this.handleTranslationChange();
          }
        }
        if (mutation.type === 'childList') {
          // Check if translated content was added
          this.handleTranslationChange();
        }
      }
    });

    this.translateObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'lang'],
      childList: true,
      subtree: false,
    });

    // Also observe body for translation changes
    this.translateObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
      childList: false,
      subtree: false,
    });

    // Poll for cookie changes as backup - store interval ID for cleanup
    this.cookieCheckInterval = setInterval(() => this.checkGoogleTranslateCookie(), 2000);
  }

  // Store interval ID for cleanup
  private cookieCheckInterval: ReturnType<typeof setInterval> | null = null;

  /**
   * Handle when translation changes
   */
  private handleTranslationChange(): void {
    const translateLang = this.getGoogleTranslateLanguage();
    const htmlLang = document.documentElement.lang;
    const detectedLang = translateLang || htmlLang || this.detectedBrowserLanguage;
    
    if (detectedLang !== this.currentLanguage) {
      this.setCurrentLanguage(detectedLang);
    }
  }

  /**
   * Set the current language and notify listeners
   */
  private setCurrentLanguage(lang: string): void {
    const normalizedLang = lang.toLowerCase().split('-')[0];
    if (normalizedLang !== this.currentLanguage) {
      this.currentLanguage = normalizedLang;
      this.applyRTL();
      this.notifyListeners();
      console.log('[LanguageEngine] Language changed to:', normalizedLang);
    }
  }

  /**
   * Apply RTL direction if needed
   */
  private applyRTL(): void {
    const isRTL = this.isRTLLanguage();
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.body.dir = isRTL ? 'rtl' : 'ltr';
  }

  /**
   * Check if current language is RTL
   */
  isRTLLanguage(): boolean {
    return RTL_LANGUAGES.includes(this.currentLanguage);
  }

  /**
   * Get current language code
   */
  getCurrentLanguage(): string {
    return this.currentLanguage;
  }

  /**
   * Get detected browser language
   */
  getDetectedBrowserLanguage(): string {
    return this.detectedBrowserLanguage;
  }

  /**
   * Get language info
   */
  getLanguageInfo(code?: string): LanguageInfo {
    const lang = code || this.currentLanguage;
    return LANGUAGE_DATABASE[lang] || LANGUAGE_DATABASE['en'];
  }

  /**
   * Get character width ratio for current language
   * Used for text fitting calculations
   */
  getCharWidthRatio(): number {
    const info = this.getLanguageInfo();
    return info.avgCharWidth;
  }

  /**
   * Subscribe to language changes
   */
  subscribe(callback: (lang: string) => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of language change
   */
  private notifyListeners(): void {
    this.listeners.forEach(callback => callback(this.currentLanguage));
  }

  /**
   * Calculate recommended font size based on text length and container width
   */
  calculateFontSize(
    text: string,
    containerWidth: number,
    baseFontSize: number,
    minFontSize: number = 12,
    maxLines: number = 2
  ): number {
    if (!text || containerWidth <= 0) return baseFontSize;

    const charWidthRatio = this.getCharWidthRatio();
    const avgCharWidth = baseFontSize * 0.6 * charWidthRatio; // Approximate character width
    const textWidth = text.length * avgCharWidth;
    const maxWidth = containerWidth * maxLines * 0.9; // 90% of available space

    if (textWidth <= maxWidth) {
      return baseFontSize;
    }

    // Calculate required font size
    const ratio = maxWidth / textWidth;
    const newFontSize = Math.max(minFontSize, Math.floor(baseFontSize * ratio));
    
    return newFontSize;
  }

  /**
   * Auto-trigger Google Translate to user's browser language
   */
  autoTranslate(): void {
    const browserLang = this.detectedBrowserLanguage;
    
    // Don't auto-translate if already in English or same as browser
    if (browserLang === 'en' || browserLang === this.currentLanguage) {
      return;
    }

    // Check if Google Translate is loaded
    const translateElement = document.querySelector('.goog-te-combo') as HTMLSelectElement;
    if (translateElement) {
      // Try to select the browser language
      const options = translateElement.querySelectorAll('option');
      for (const option of Array.from(options)) {
        if (option.value === browserLang || option.value.startsWith(browserLang)) {
          translateElement.value = option.value;
          translateElement.dispatchEvent(new Event('change'));
          console.log('[LanguageEngine] Auto-translated to:', browserLang);
          break;
        }
      }
    }
  }

  /**
   * Cleanup - call this when the app unmounts
   */
  destroy(): void {
    if (this.translateObserver) {
      this.translateObserver.disconnect();
      this.translateObserver = null;
    }
    if (this.cookieCheckInterval) {
      clearInterval(this.cookieCheckInterval);
      this.cookieCheckInterval = null;
    }
    this.listeners.clear();
    this.isInitialized = false;
  }
}

// Singleton instance
export const LanguageEngine = new LanguageEngineClass();

// Export type for React context
export type { LanguageEngineClass };
